import axiosInstance from "../../../../../services/api/axiosInstance";

// ============================================================================
// SUBJECT CRUD OPERATIONS
// ============================================================================

/**
 * Get all subjects
 */
export const getAllSubjects = async (schoolId = null, subjectType = null, isElective = null) => {
    try {
        let url = `/subject`;
        const params = new URLSearchParams();

        if (schoolId) params.append('schoolId', schoolId);
        if (subjectType) params.append('subjectType', subjectType);
        if (isElective !== null) params.append('isElective', isElective);

        if (params.toString()) url += `?${params.toString()}`;

        const response = await axiosInstance.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching subjects:", error);
        throw error;
    }
};

/**
 * Get subject by ID
 */
export const getSubjectById = async (id) => {
    try {
        const response = await axiosInstance.get(`/subject/${id}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching subject:", error);
        throw error;
    }
};

/**
 * Create new subject
 */
export const createSubject = async (data) => {
    try {
        const response = await axiosInstance.post(`/subject`, data);
        return response.data;
    } catch (error) {
        console.error("Error creating subject:", error);
        throw error;
    }
};

/**
 * Update subject
 */
export const updateSubject = async (id, data) => {
    try {
        const response = await axiosInstance.put(`/subject/${id}`, data);
        return response.data;
    } catch (error) {
        console.error("Error updating subject:", error);
        throw error;
    }
};

/**
 * Delete subject
 */
export const deleteSubject = async (id) => {
    try {
        const response = await axiosInstance.delete(`/subject/${id}`);
        return response.data;
    } catch (error) {
        console.error("Error deleting subject:", error);
        throw error;
    }
};

/**
 * Bulk create subjects
 */
export const bulkCreateSubjects = async (subjects) => {
    try {
        const response = await axiosInstance.post(`/subject/bulk`, subjects);
        return response.data;
    } catch (error) {
        console.error("Error bulk creating subjects:", error);
        throw error;
    }
};

// ============================================================================
// CLASS SUBJECT OPERATIONS
// ============================================================================

/**
 * Get subjects for a class
 */
export const getClassSubjects = async (classId, academicYear = null) => {
    try {
        let url = `/class-subjects/class/${classId}`;
        if (academicYear) url += `?academicYear=${academicYear}`;

        const response = await axiosInstance.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching class subjects:", error);
        throw error;
    }
};

/**
 * Assign subjects to class
 */
export const assignSubjectsToClass = async (data) => {
    try {
        const response = await axiosInstance.post(`/class-subjects`, data);
        return response.data;
    } catch (error) {
        console.error("Error assigning subjects to class:", error);
        throw error;
    }
};

/**
 * Update class subjects
 */
export const updateClassSubjects = async (classId, data) => {
    try {
        const response = await axiosInstance.put(`/class-subjects/class/${classId}`, data);
        return response.data;
    } catch (error) {
        console.error("Error updating class subjects:", error);
        throw error;
    }
};

/**
 * Remove subject from class
 */
export const removeSubjectFromClass = async (classSubjectId) => {
    try {
        const response = await axiosInstance.delete(`/class-subjects/${classSubjectId}`);
        return response.data;
    } catch (error) {
        console.error("Error removing subject from class:", error);
        throw error;
    }
};

/**
 * Toggle subject active status
 */
export const toggleSubjectActive = async (classSubjectId) => {
    try {
        const response = await axiosInstance.patch(`/class-subjects/${classSubjectId}/toggle-active`);
        return response.data;
    } catch (error) {
        console.error("Error toggling subject status:", error);
        throw error;
    }
};

// ============================================================================
// ELECTIVE GROUP OPERATIONS
// ============================================================================

/**
 * Get elective groups for a class
 */
export const getElectiveGroups = async (classId, academicYear = null, activeOnly = true) => {
    try {
        let url = `/elective-groups/class/${classId}`;
        const params = new URLSearchParams();

        if (academicYear) params.append('academicYear', academicYear);
        params.append('activeOnly', activeOnly);

        if (params.toString()) url += `?${params.toString()}`;

        const response = await axiosInstance.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching elective groups:", error);
        throw error;
    }
};

/**
 * Get elective group by ID
 */
export const getElectiveGroupById = async (id) => {
    try {
        const response = await axiosInstance.get(`/elective-groups/${id}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching elective group:", error);
        throw error;
    }
};

/**
 * Create elective group
 */
export const createElectiveGroup = async (data) => {
    try {
        const response = await axiosInstance.post(`/elective-groups`, data);
        return response.data;
    } catch (error) {
        console.error("Error creating elective group:", error);
        throw error;
    }
};

/**
 * Update elective group
 */
export const updateElectiveGroup = async (id, data) => {
    try {
        const response = await axiosInstance.put(`/elective-groups/${id}`, data);
        return response.data;
    } catch (error) {
        console.error("Error updating elective group:", error);
        throw error;
    }
};

/**
 * Delete elective group
 */
export const deleteElectiveGroup = async (id) => {
    try {
        const response = await axiosInstance.delete(`/elective-groups/${id}`);
        return response.data;
    } catch (error) {
        console.error("Error deleting elective group:", error);
        throw error;
    }
};

/**
 * Add subjects to elective group
 */
export const addSubjectsToGroup = async (groupId, subjectIds) => {
    try {
        const response = await axiosInstance.post(`/elective-groups/${groupId}/subjects`, subjectIds);
        return response.data;
    } catch (error) {
        console.error("Error adding subjects to group:", error);
        throw error;
    }
};

/**
 * Remove subject from group
 */
export const removeSubjectFromGroup = async (groupId, subjectId) => {
    try {
        const response = await axiosInstance.delete(`/elective-groups/${groupId}/subjects/${subjectId}`);
        return response.data;
    } catch (error) {
        console.error("Error removing subject from group:", error);
        throw error;
    }
};

/**
 * Toggle group active status
 */
export const toggleGroupActive = async (groupId) => {
    try {
        const response = await axiosInstance.patch(`/elective-groups/${groupId}/toggle-active`);
        return response.data;
    } catch (error) {
        console.error("Error toggling group status:", error);
        throw error;
    }
};

// ============================================================================
// STUDENT SUBJECT OPERATIONS
// ============================================================================

/**
 * Get student subjects
 */
export const getStudentSubjects = async (studentId, academicYear = null) => {
    try {
        let url = `/student-subjects/student/${studentId}`;
        if (academicYear) url += `?academicYear=${academicYear}`;

        const response = await axiosInstance.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching student subjects:", error);
        throw error;
    }
};

/**
 * Assign elective subjects to student
 */
export const assignElectiveSubjects = async (data) => {
    try {
        const response = await axiosInstance.post(`/student-subjects/assign`, data);
        return response.data;
    } catch (error) {
        console.error("Error assigning elective subjects:", error);
        throw error;
    }
};

/**
 * Update student elective subjects
 */
export const updateStudentElectives = async (studentId, subjectIds, academicYear = null) => {
    try {
        let url = `/student-subjects/student/${studentId}/electives`;
        if (academicYear) url += `?academicYear=${academicYear}`;

        const response = await axiosInstance.put(url, subjectIds);
        return response.data;
    } catch (error) {
        console.error("Error updating student electives:", error);
        throw error;
    }
};

/**
 * Validate elective selection
 */
export const validateElectiveSelection = async (data) => {
    try {
        const response = await axiosInstance.post(`/student-subjects/validate-selection`, data);
        return response.data;
    } catch (error) {
        console.error("Error validating selection:", error);
        throw error;
    }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get current academic year
 */
export const getCurrentAcademicYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // 0-indexed

    if (month >= 4) {
        return `${year}-${year + 1}`;
    } else {
        return `${year - 1}-${year}`;
    }
};

/**
 * Get all classes (you'll need to create this endpoint)
 */
export const getAllClasses = async () => {
    try {
        const response = await axiosInstance.get(`/classes`);
        return response.data;
    } catch (error) {
        console.error("Error fetching classes:", error);
        throw error;
    }
};

// Get available elective groups
export const getAvailableElectiveGroups = async (classId, academicYear) => {
    try {
        const response = await axiosInstance.get(`/student-electives/available`, {
            params: { classId, academicYear },
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching classes:", error);
        throw error;
    }
};

// Get student's elective selections
export const getStudentElectiveSelections = async (studentId) => {
    try {
        const response = await axiosInstance.get(`/student-electives/${studentId}`);
        return response.data;
    } catch (error) {
        console.error("Error fetching classes:", error);
        throw error;
    }
};

// Assign elective subjects to student
export const assignStudentElectives = async (studentId, selections) => {
    try {
        const response = await axiosInstance.post('/api/student-electives/assign', {
            studentId,
            selections
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching classes:", error);
        throw error;
    }
};